import { CREDIT_BLOCK_SYSTEM_CASE_ORIGIN } from './caseOriginDisplay';

const CUSTOM_SUFFIX = process.env.REACT_APP_GOBODO_CUSTOM_SUFFIX ?? '__c';

/** Sub-type values aligned with Credit Block case picklists (Gobodo `Sub_Type__c`). */
export const CREDIT_BLOCK_CASE_SUBTYPES = [
  'Equifax Unblock',
  'Experian Unblock',
  'TransUnion Unblock',
  'ChexSystems',
  'Lock / Block',
  'Unlock',
  'Credit pull',
  'Extension',
  'Special case',
  'Permanent unblock',
  'Other',
] as const;

export const CREDIT_BLOCK_CASE_ORIGINS = ['Email', 'Web', 'Phone', CREDIT_BLOCK_SYSTEM_CASE_ORIGIN] as const;

export type CreditBlockCaseSubType = (typeof CREDIT_BLOCK_CASE_SUBTYPES)[number];
export type CreditBlockCaseOrigin = (typeof CREDIT_BLOCK_CASE_ORIGINS)[number];

export interface CreditBlockCaseCreateInput {
  clientId: string;
  clientName: string;
  /** Display / text owner (always sent as Owner__c when set). */
  owner: string;
  /**
   * Gobodo User **graph object key** when chosen as `u:{key}` from assignee search (same value as
   * `REACT_APP_GOBODO_GRAPH_USER_KEY` / traversal start key). Not Cognito/auth id unless they match.
   */
  ownerUserId?: string;
  subject: string;
  subType: string;
  priority: string;
  origin: string;
  description: string;
}

/**
 * Payload for `createRecord('Case__c', …)` — sends both `__c` and camel aliases where the app
 * already normalizes Case rows (see creditBlockCaseQueries `toListViewRecord`).
 * Relationship keys use the configured Gobodo custom suffix.
 */
const ownerRelKey = `OwnerRelField${CUSTOM_SUFFIX}`;

/** Case skin field for Unlock_Request → Case (`unlock_request_case_rel`); last-linked UR wins when many per case. */
export const UNLOCK_REQUEST_REL_FIELD_ON_CASE = `UnlockRequestRelField${CUSTOM_SUFFIX}`;

/** Gobodo graph key/id from a createRecord response (prefer `key`, then `id`). */
export function graphKeyFromCreatedRecord(rec: { id?: unknown; key?: unknown }): string {
  return String(rec.key ?? rec.id ?? '').trim();
}

/**
 * Merge owner display name + optional Gobodo user graph key into a Case update payload (or partial create).
 * `ownerPickRaw`: assignee SearchableSelect value, e.g. `u:{graphUserKey}`; plain-name picks do not set id/relationship fields.
 *
 * Only sets `OwnerRelField${CUSTOM_SUFFIX}` for the assignee edge (setup: case_assignee_rel skin). Omitting
 * `OwnerId__c` / `OwnerRelField__c` avoids duplicate or unknown-field errors when the env suffix is not `__c`.
 */
export function applyCaseOwnerGraphFields(
  payload: Record<string, unknown>,
  options: { ownerPickRaw?: string; ownerDisplayName: string }
): void {
  const { ownerPickRaw, ownerDisplayName } = options;
  payload.Owner__c = ownerDisplayName;
  payload.owner = ownerDisplayName;
  const raw = ownerPickRaw?.trim() ?? '';
  const uid = raw.startsWith('u:') ? raw.slice(2).trim() : '';
  if (!uid) return;
  payload[ownerRelKey] = uid;
}

export function buildCreditBlockCaseCreatePayload(input: CreditBlockCaseCreateInput): Record<string, unknown> {
  const accountRelKey = `AccountRelField${CUSTOM_SUFFIX}`;
  const normalizedOwner = String(input.owner ?? '').trim();
  const initialStatus = normalizedOwner ? 'Assigned' : 'New';
  const payload: Record<string, unknown> = {
    Subject__c: input.subject,
    subject: input.subject,
    Type__c: 'Credit Block',
    type: 'Credit Block',
    Sub_Type__c: input.subType,
    subType: input.subType,
    Priority__c: input.priority,
    priority: input.priority,
    Status__c: initialStatus,
    status: initialStatus,
    Origin__c: input.origin,
    origin: input.origin,
    ...(normalizedOwner ? { Owner__c: normalizedOwner, owner: normalizedOwner } : {}),
    ...(input.ownerUserId?.trim() ? { [ownerRelKey]: input.ownerUserId.trim() } : {}),
    AccountId__c: input.clientId,
    accountId: input.clientId,
    AccountName__c: input.clientName,
    accountName: input.clientName,
    Client__c: input.clientId,
    [accountRelKey]: input.clientId,
  };
  const desc = input.description.trim();
  if (desc) {
    payload.Description__c = desc;
    payload.description = desc;
  }
  return payload;
}
